/**
 * Card Component Suite
 *
 * Purpose: Provides a flexible card layout system for organizing content in containers.
 *
 * Features:
 * - Multiple card components (Card, CardHeader, CardContent, CardTitle, CardDescription, CardFooter)
 * - Hover effects and animations
 * - Consistent spacing and styling
 * - Responsive design support
 * - Shadow effects with design system integration
 *
 * Components:
 * @param Card - Main container with border, shadow, and hover effects
 * @param CardHeader - Top section for titles and descriptions with consistent spacing
 * @param CardContent - Main content area with appropriate padding
 * @param CardTitle - Styled heading text for card titles
 * @param CardDescription - Muted text for card descriptions
 * @param CardFooter - Bottom section for actions or additional info
 *
 * Parameters:
 * All components accept standard HTML div attributes:
 * @param className (optional) - Additional CSS classes
 * @param children (mandatory) - Card content
 *
 * Usage Pattern:
 * <Card>
 *   <CardHeader>
 *     <CardTitle>Title</CardTitle>
 *     <CardDescription>Description</CardDescription>
 *   </CardHeader>
 *   <CardContent>Content</CardContent>
 *   <CardFooter>Footer content</CardFooter>
 * </Card>
 */

import * as React from 'react';

import { cn } from '../lib/utils';

const Card = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    variant?: 'default' | 'nested' | 'elevated';
  }
>(({ className, variant = 'default', ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'rounded-lg border text-card-foreground transition-all duration-300 hover:translate-y-[-1px] ',
      {
        'bg-card backdrop-blur-sm shadow-soft hover:shadow-medium ':
          variant === 'default',
        'bg-card-nested border-card-nested-border backdrop-blur-sm shadow-soft hover:shadow-medium ':
          variant === 'nested',
        'bg-white backdrop-blur-3xl shadow-large border-primary border-2 space-y-4 transition-all  hover:shadow-xl hover:border-primary/80':
          variant === 'elevated',
      },
      className,
    )}
    {...props}
  />
));
Card.displayName = 'Card';

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex flex-col space-y-1 p-4', className)}
    {...props}
  />
));
CardHeader.displayName = 'CardHeader';

const CardTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn(
      'text-xl font-semibold leading-none tracking-tight',
      className,
    )}
    {...props}
  />
));
CardTitle.displayName = 'CardTitle';

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));
CardDescription.displayName = 'CardDescription';

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('p-4 pt-0', className)} {...props} />
));
CardContent.displayName = 'CardContent';

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex items-center p-6 pt-0', className)}
    {...props}
  />
));
CardFooter.displayName = 'CardFooter';

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardDescription,
  CardContent,
};
